// skipValidation.js
//
// Pure helpers for tracking bypassed (skipped-by-logic) questions and
// validating required questions at survey submit time.
//
// The feature this supports: a skip rule legitimately bypasses a required
// question. Historically the client blocked forward skips over required
// questions and re-validated every required question at submit. That flagged
// perfectly valid paths as errors whenever skip-logic sent the participant
// past a required screen. New contract:
//
//   A required question is considered "missing" only if BOTH:
//     - it's in visitedQuestionIds (participant actually saw the screen)
//     - it's NOT in skippedQuestionIds (no skip rule bypassed it)
//
// Questions never visited AND not skipped-by-logic are fine — the participant
// simply never reached them. A required question in visitedQuestionIds
// without an answer indicates the participant clicked past without answering
// (still a blocker).

/**
 * Resolve a stable question identifier for an element. Prefers
 * `element.questionId` (backend DB id) and falls back to `element.id`
 * (canvas element id) — matching `getResponseKey` in skipLogic.js.
 * Returns null for non-question elements or elements missing both keys.
 */
export const getElementQuestionKey = (element) => {
  if (!element || element.type !== 'question') return null;
  if (element.questionId != null) return element.questionId;
  if (element.id != null) return element.id;
  return null;
};

/**
 * Collect question IDs on a single screen.
 *
 * Live survey screens (loadAllScreens) have numeric `questionId`; preview
 * canvas elements may fall back to `element.id`. We accept both via
 * `getElementQuestionKey`.
 */
export const collectScreenQuestionIds = (screen) => {
  if (!screen?.elements) return [];
  const ids = [];
  for (const el of screen.elements) {
    const key = getElementQuestionKey(el);
    if (key != null) ids.push(key);
  }
  return ids;
};

/**
 * Compute which screens are BYPASSED by a forward skip from currentIndex to
 * targetIndex. A screen is bypassed when currentIndex < idx < targetIndex.
 *
 * Backward skips (target <= current+1) bypass nothing — the participant is
 * returning, not skipping forward.
 *
 * Returns an array of screen objects (or empty array).
 */
export const computeBypassedScreens = (currentIndex, targetIndex, screens) => {
  if (!Array.isArray(screens) || screens.length === 0) return [];
  if (targetIndex <= currentIndex + 1) return []; // no forward bypass
  const bypassed = [];
  for (let i = currentIndex + 1; i < targetIndex; i++) {
    if (screens[i]) bypassed.push(screens[i]);
  }
  return bypassed;
};

/**
 * Given a forward skip from currentIndex to targetIndex (both screen indexes
 * into `screens`), build the list of skip-record entries for every question
 * on every bypassed screen. Each entry has shape:
 *   { question_id, skipped_by_rule_id }
 *
 * Backward skips produce an empty list (nothing was bypassed).
 */
export const buildSkipRecordsForBypass = (
  currentIndex,
  targetIndex,
  screens,
  firingRuleId
) => {
  const bypassed = computeBypassedScreens(currentIndex, targetIndex, screens);
  const records = [];
  for (const screen of bypassed) {
    for (const qid of collectScreenQuestionIds(screen)) {
      records.push({
        question_id: qid,
        skipped_by_rule_id: firingRuleId == null ? null : String(firingRuleId),
      });
    }
  }
  return records;
};

/**
 * Merge new skip records into an existing list, de-duplicated by question_id.
 * First-writer wins: if a question is already marked skipped (by any rule),
 * a later skip-over doesn't overwrite the attribution.
 */
export const mergeSkipRecords = (existing, incoming) => {
  const seen = new Set();
  const out = [];
  for (const r of existing || []) {
    const key = String(r.question_id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  for (const r of incoming || []) {
    const key = String(r.question_id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

/**
 * Extract the Set of question IDs from a list of skip records.
 * Used to test "is this question skipped-by-logic?" in O(1).
 */
export const skippedQuestionIdSet = (skippedRecords) => {
  const s = new Set();
  for (const r of skippedRecords || []) {
    if (r?.question_id != null) s.add(String(r.question_id));
  }
  return s;
};

/**
 * Predicate: does a response count as "answered"?
 * Matches the existing SurveyTaking logic — empty string, null, undefined,
 * empty array all count as unanswered. `0` and `false` count as answered.
 */
export const isAnswered = (value) => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

/**
 * Predicate returning true if a screen element is a required question
 * per the screen's navigationRules metadata.
 *
 * navigationRules shape on screens from loadAllScreens():
 *   { [questionId]: { is_required: boolean, skip_rules: [...], ... } }
 *
 * PreviewMode uses `element.required` directly on the element. Both shapes
 * are supported for convenience (pass whichever the caller has).
 */
export const isElementRequired = (element, navigationRules) => {
  if (navigationRules && element?.questionId != null) {
    const rule = navigationRules[String(element.questionId)];
    if (rule?.is_required === true) return true;
    if (rule?.is_required === false) return false;
  }
  return element?.required === true;
};

/**
 * Submit-time required validation.
 *
 * A required question is INVALID (i.e. submit should block) when:
 *   - It's in visitedQuestionIds (participant saw it), AND
 *   - It's NOT in skippedQuestionIds (no skip rule bypassed it), AND
 *   - It has no answered response
 *
 * Returns an array of question IDs (as strings) that are missing.
 *
 * @param {object[]} screens - all screens the survey could render
 * @param {object} responses - responses keyed by questionId (string or number)
 * @param {Set<string>|Iterable<any>} visitedQuestionIds - IDs the participant saw
 * @param {Set<string>|Iterable<any>} skippedQuestionIds - IDs skipped by logic
 * @param {object} [opts]
 * @param {(element: object) => boolean} [opts.isRequired] - override for required check
 */
export const validateRequiredAtSubmit = (
  screens,
  responses,
  visitedQuestionIds,
  skippedQuestionIds,
  opts = {}
) => {
  const visited = visitedQuestionIds instanceof Set
    ? visitedQuestionIds
    : new Set(Array.from(visitedQuestionIds || []).map(String));
  const skipped = skippedQuestionIds instanceof Set
    ? skippedQuestionIds
    : new Set(Array.from(skippedQuestionIds || []).map(String));

  const missing = [];
  for (const screen of screens || []) {
    const navRules = screen?.navigationRules || null;
    for (const el of screen?.elements || []) {
      if (el.type !== 'question') continue;
      const qid = getElementQuestionKey(el);
      if (qid == null) continue;
      const key = String(qid);

      const required = opts.isRequired
        ? opts.isRequired(el)
        : isElementRequired(el, navRules);
      if (!required) continue;

      // Skipped-by-logic questions are always allowed, even if visited
      // (e.g. participant answered, then a later skip rule bypassed the
      // screen on a re-visit). Backend applies the same rule.
      if (skipped.has(key)) continue;

      // Unvisited and not skipped-by-logic → participant never reached it,
      // fine. (Happens with legitimate forward nav that ended the survey
      // before reaching a later required question.)
      if (!visited.has(key)) continue;

      // Visited and not skipped → must be answered.
      const answer = responses?.[key] ?? responses?.[qid];
      if (!isAnswered(answer)) missing.push(key);
    }
  }
  return missing;
};
